import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";

import { Box } from "@mui/material";
import { Stage, Layer, Rect, Group, Transformer } from "react-konva";
import Konva from "konva";
import ReactPlayer from "react-player";

import {
  GifOverlay as GifOverlayType,
  TextOverlay as TextOverlayType,
  Paragraph,
  SentenceObject,
  SubtitleStyles,
  BackgroundStyle as BackgroundStyleEnum,
  REFERENCE_PREVIEW_HEIGHT,
} from "../../types";
import { updateShortVideo } from "../../services/edit-short-video.service";
import { AspectRatio } from "../AspectRatioSelector";
import SubtitleRenderer from "./SubtitleRenderer";
import GifOverlay from "./GifOverlay";
import TextOverlayRenderer from "./TextOverlayRenderer";

interface SubtitleEvent {
  text: string;
  startSec: number;
  endSec: number;
}

const WORDS_PER_GROUP = 3;

function buildSubtitleEvents(
  paragraphs: Paragraph[],
  wordsPerGroup = WORDS_PER_GROUP,
): SubtitleEvent[] {
  const allWords: SentenceObject[] = paragraphs.flatMap((p) => p.words);
  const events: SubtitleEvent[] = [];

  for (let i = 0; i < allWords.length; i += wordsPerGroup) {
    const group = allWords.slice(i, i + wordsPerGroup);
    const firstStart = group[0]?.start;
    const lastEnd = group[group.length - 1]?.end;

    if (firstStart != null && lastEnd != null) {
      events.push({
        text: group.map((w) => w.word).join(" "),
        startSec: firstStart,
        endSec: lastEnd,
      });
    }
  }

  return events;
}

interface Props {
  videoId: string;
  url: string;
  paragraphs: Paragraph[];
  isPlaying: boolean;
  toggleIsPlaying: () => void;
  subtitleStyles: SubtitleStyles;
  onTimeUpdate: (timeInMilliseconds: number) => void;
  currentTimeInMilliseconds: number;
  onControlsReady: (controls: {
    play: () => void;
    pause: () => void;
    seekTo: (time: number) => void;
    getDuration: () => number;
  }) => void;
  onUpdateStyles: (styles: SubtitleStyles) => void;
  aspectRatio: AspectRatio;
  onVideoEnd?: () => void;
  gifOverlays?: GifOverlayType[];
  onUpdateGif?: (id: string, updates: Partial<GifOverlayType>) => void;
  textOverlays?: TextOverlayType[];
  onUpdateText?: (id: string, updates: Partial<TextOverlayType>) => void;
  cropPositionX?: number;
  cropPositionY?: number;
  onCropPositionChange?: (x: number, y: number) => void;
}

export default function VideoPlayer({
  videoId,
  url,
  paragraphs,
  isPlaying,
  toggleIsPlaying,
  subtitleStyles,
  onTimeUpdate,
  currentTimeInMilliseconds,
  onControlsReady,
  onUpdateStyles,
  aspectRatio,
  onVideoEnd,
  gifOverlays = [],
  onUpdateGif,
  textOverlays = [],
  onUpdateText,
  cropPositionX = 50,
  cropPositionY = 50,
  onCropPositionChange,
}: Props) {
  const playerRef = useRef<ReactPlayer>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [videoError, setVideoError] = useState<string | null>(null);

  // Calculate video dimensions based on aspect ratio
  const getVideoDimensions = useCallback(() => {
    const maxWidth = 800; // Larger when collapsed
    const maxHeight = 600; // Larger when collapsed
    const minWidth = 400;
    const minHeight = 300;

    const targetAspectRatio = aspectRatio.width / aspectRatio.height;

    let width = maxWidth;
    let height = width / targetAspectRatio;

    if (height > maxHeight) {
      height = maxHeight;
      width = height * targetAspectRatio;
    }

    // Ensure minimum size
    if (width < minWidth) {
      width = minWidth;
      height = width / targetAspectRatio;
    }
    if (height < minHeight) {
      height = minHeight;
      width = height * targetAspectRatio;
    }

    return { width, height };
  }, [aspectRatio]);

  const videoDimensions = getVideoDimensions();
  const videoDimensionsRef = useRef(videoDimensions);

  // Update dimensions when aspect ratio changes
  useEffect(() => {
    const newDimensions = getVideoDimensions();
    videoDimensionsRef.current = newDimensions;
  }, [aspectRatio, getVideoDimensions]);

  // Refs for Konva elements
  const rectRef = useRef<Konva.Rect>(null);
  const transformerRef = useRef<Konva.Transformer>(null);
  const layerRef = useRef<Konva.Layer>(null);

  // State variables for the text handle position and size
  const [textHandlePositionXPercentage, setTextHandlePositionXPercentage] =
    useState<number>(subtitleStyles?.text_handle_position_x_percentage ?? 0);
  const [textHandlePositionYPercentage, setTextHandlePositionYPercentage] =
    useState<number>(subtitleStyles?.text_handle_position_y_percentage ?? 60);
  const [textHandleWidthPercentage, setTextHandleWidthPercentage] =
    useState<number>(subtitleStyles?.text_handle_width_percentage ?? 100);

  // Update subtitle positions when aspect ratio changes to maintain relative positioning
  useEffect(() => {
    if (subtitleStyles) {
      setTextHandlePositionXPercentage(
        subtitleStyles.text_handle_position_x_percentage ?? 0,
      );
      setTextHandlePositionYPercentage(
        subtitleStyles.text_handle_position_y_percentage ?? 60,
      );
      setTextHandleWidthPercentage(
        subtitleStyles.text_handle_width_percentage ?? 100,
      );
    }
  }, [subtitleStyles, aspectRatio]);

  const subtitleEvents = useMemo(
    () => buildSubtitleEvents(paragraphs),
    [paragraphs],
  );

  const activeSubtitleEvent = useMemo(() => {
    const t = currentTimeInMilliseconds / 1000;
    return subtitleEvents.find((e) => t >= e.startSec && t <= e.endSec) ?? null;
  }, [subtitleEvents, currentTimeInMilliseconds]);

  const handleRectHeight = useMemo(() => {
    const fontScale = videoDimensions.height / REFERENCE_PREVIEW_HEIGHT;
    const scaledSize = Math.round((subtitleStyles?.size || 24) * fontScale);
    const lineHeight = subtitleStyles?.line_height || 1.2;
    const scaledPadding = Math.round(40 * fontScale);

    const textLength = activeSubtitleEvent?.text.length ?? 0;
    const boxWidth = (textHandleWidthPercentage / 100) * videoDimensions.width;
    const charsPerLine = Math.floor(
      (boxWidth - scaledPadding) / (scaledSize * 0.6),
    );
    const estimatedLines = Math.max(
      1,
      textLength > 0 ? Math.ceil(textLength / charsPerLine) : 2,
    );
    return Math.max(
      Math.round(60 * fontScale),
      Math.floor(scaledSize * lineHeight * estimatedLines) + scaledPadding,
    );
  }, [
    subtitleStyles?.size,
    subtitleStyles?.line_height,
    activeSubtitleEvent,
    textHandleWidthPercentage,
    videoDimensions.width,
    videoDimensions.height,
  ]);

  useEffect(() => {
    if (playerRef.current) {
      onControlsReady({
        play: () => {
          const internalPlayer = playerRef.current?.getInternalPlayer();
          if (internalPlayer) {
            internalPlayer.play();
          }
        },
        pause: () => playerRef.current?.getInternalPlayer().pause(),
        seekTo: (time: number) => playerRef.current?.seekTo(time, "seconds"),
        getDuration: () => playerRef.current?.getDuration() || 0,
      });
    }
  }, [onControlsReady]);

  const handleProgress = useCallback(
    (progress: { playedSeconds: number }) => {
      onTimeUpdate(progress.playedSeconds * 1000);
    },
    [onTimeUpdate],
  );

  const handleReady = useCallback(() => {
    setIsVideoReady(true);
    setVideoError(null);
  }, []);

  useEffect(() => {
    const internal = playerRef.current?.getInternalPlayer() as
      | HTMLVideoElement
      | undefined;
    if (internal) {
      internal.style.objectPosition = `${cropPositionX}% ${cropPositionY}%`;
    }
  }, [cropPositionX, cropPositionY]);

  const videoDragRef = useRef<{
    dragging: boolean;
    startX: number;
    startY: number;
    startCropX: number;
    startCropY: number;
  } | null>(null);

  const handleVideoDragStart = useCallback(
    (e: React.MouseEvent) => {
      if (!onCropPositionChange) return;
      videoDragRef.current = {
        dragging: false,
        startX: e.clientX,
        startY: e.clientY,
        startCropX: cropPositionX,
        startCropY: cropPositionY,
      };

      const onMouseMove = (ev: MouseEvent) => {
        if (!videoDragRef.current) return;
        const dx = ev.clientX - videoDragRef.current.startX;
        const dy = ev.clientY - videoDragRef.current.startY;
        if (!videoDragRef.current.dragging && Math.abs(dx) + Math.abs(dy) > 4) {
          videoDragRef.current.dragging = true;
        }
        if (videoDragRef.current.dragging) {
          const sensitivityX = 100 / videoDimensions.width;
          const sensitivityY = 100 / videoDimensions.height;
          const newX = Math.min(
            100,
            Math.max(0, videoDragRef.current.startCropX - dx * sensitivityX),
          );
          const newY = Math.min(
            100,
            Math.max(0, videoDragRef.current.startCropY - dy * sensitivityY),
          );
          onCropPositionChange(newX, newY);
        }
      };

      const onMouseUp = () => {
        if (videoDragRef.current && !videoDragRef.current.dragging) {
          toggleIsPlaying();
        }
        videoDragRef.current = null;
        window.removeEventListener("mousemove", onMouseMove);
        window.removeEventListener("mouseup", onMouseUp);
      };

      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);
    },
    [
      onCropPositionChange,
      cropPositionX,
      cropPositionY,
      videoDimensions,
      toggleIsPlaying,
    ],
  );

  const handleError = useCallback((error: any) => {
    setVideoError(error?.message || "Failed to load video");
    setIsVideoReady(false);
  }, []);

  const handleEnded = useCallback(() => {
    // When video ends, just stop playing - don't seek to beginning
    // The parent component will handle the isPlaying state through toggleIsPlaying
    if (onVideoEnd) {
      onVideoEnd();
    }
  }, [onVideoEnd]);

  const handleDragEnd = useCallback(
    (e: Konva.KonvaEventObject<DragEvent>) => {
      const node = e.target;
      const newX = node.x();
      const newY = node.y();

      // Convert absolute positions to percentages based on the actual video dimensions
      const newXPercentage = (newX / videoDimensions.width) * 100;
      const newYPercentage = (newY / videoDimensions.height) * 100;

      setTextHandlePositionXPercentage(newXPercentage);
      setTextHandlePositionYPercentage(newYPercentage);

      const updatedStyles = {
        ...subtitleStyles,
        text_handle_position_x_percentage: newXPercentage,
        text_handle_position_y_percentage: newYPercentage,
      };
      onUpdateStyles(updatedStyles);

      updateShortVideo(videoId, { subtitleStyles: updatedStyles });
    },
    [videoId, subtitleStyles, onUpdateStyles, videoDimensions],
  );

  const handleTransform = useCallback(() => {
    if (rectRef.current) {
      const node = rectRef.current;
      const scaleX = node.scaleX();
      const width = Math.max(5, node.width() * scaleX);

      node.scaleX(1);
      node.scaleY(1);
      node.width(width);

      const newWidthPercentage = (width / videoDimensions.width) * 100;
      setTextHandleWidthPercentage(newWidthPercentage);
    }
  }, [videoDimensions]);

  const handleTransformEnd = useCallback(() => {
    if (rectRef.current) {
      const node = rectRef.current;
      const width = node.width();
      const newWidthPercentage = (width / videoDimensions.width) * 100;

      // Get the final center position
      const group = node.getParent();
      let newCenterXPercentage = textHandlePositionXPercentage;
      if (group) {
        const newCenterX = group.x();
        newCenterXPercentage = (newCenterX / videoDimensions.width) * 100;
        setTextHandlePositionXPercentage(newCenterXPercentage);
      }

      const updatedStyles = {
        ...subtitleStyles,
        text_handle_width_percentage: newWidthPercentage,
        text_handle_position_x_percentage: newCenterXPercentage,
      };
      onUpdateStyles(updatedStyles);

      updateShortVideo(videoId, { subtitleStyles: updatedStyles });
    }
  }, [
    videoId,
    subtitleStyles,
    onUpdateStyles,
    videoDimensions,
    textHandlePositionXPercentage,
  ]);

  useEffect(() => {
    const transformer = transformerRef.current;
    const rect = rectRef.current;

    if (transformer && rect) {
      transformer.nodes([rect]);
      transformer.getLayer()?.batchDraw();
    }
  }, [rectRef.current?.width()]);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        padding: 1,
        boxSizing: "border-box",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: videoDimensions.width,
          height: videoDimensions.height,
          backgroundColor: "#000",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <ReactPlayer
          ref={playerRef}
          url={url}
          playing={isPlaying}
          progressInterval={50}
          onProgress={handleProgress}
          onReady={handleReady}
          onError={handleError}
          onEnded={handleEnded}
          width={videoDimensions.width}
          height={videoDimensions.height}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            backgroundColor: "#000",
          }}
          config={{
            file: {
              attributes: {
                style: {
                  objectFit: "cover",
                  width: "100%",
                  height: "100%",
                },
              },
            },
          }}
        />
        {/* Video Loading Indicator */}
        {!isVideoReady && !videoError && (
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              color: "white",
              fontSize: "14px",
              zIndex: 1,
            }}
          >
            Loading video...
          </Box>
        )}
        {/* Video Error Indicator */}
        {videoError && (
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              color: "#ef4444",
              fontSize: "14px",
              textAlign: "center",
              zIndex: 1,
            }}
          >
            <div>Video Error: {videoError}</div>
            <div style={{ fontSize: "12px", marginTop: "8px" }}>
              Please check the video URL
            </div>
          </Box>
        )}

        <Stage
          width={videoDimensions.width}
          height={videoDimensions.height}
          scaleX={1}
          scaleY={1}
          onMouseDown={(e) => {
            const isKonvaShape = e.target !== e.target.getStage();
            if (!isKonvaShape) {
              handleVideoDragStart(e.evt as unknown as React.MouseEvent);
            }
          }}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            cursor: onCropPositionChange ? "grab" : "default",
          }}
        >
          <Layer ref={layerRef}>
            {activeSubtitleEvent && (
              <SubtitleRenderer
                key={`${activeSubtitleEvent.startSec}-${activeSubtitleEvent.endSec}`}
                text={activeSubtitleEvent.text}
                styles={subtitleStyles}
                videoDimensions={videoDimensions}
                handlePositionX={textHandlePositionXPercentage}
                handlePositionY={textHandlePositionYPercentage}
                handleWidth={textHandleWidthPercentage}
              />
            )}

            {/* Text handle for positioning (always visible at saved position) */}
            <Group
              x={(textHandlePositionXPercentage / 100) * videoDimensions.width}
              y={(textHandlePositionYPercentage / 100) * videoDimensions.height}
              draggable
              onDragEnd={handleDragEnd}
              onClick={(e) => {
                e.cancelBubble = true;
              }}
            >
              <Rect
                ref={rectRef}
                x={0}
                onTransform={handleTransform}
                onTransformEnd={handleTransformEnd}
                width={Math.max(
                  0,
                  (textHandleWidthPercentage / 100) * videoDimensions.width,
                )}
                height={handleRectHeight}
                fill="transparent"
                stroke="rgba(255,255,255,0.5)"
                strokeWidth={1}
                cornerRadius={
                  subtitleStyles?.background_style ===
                  BackgroundStyleEnum.ROUNDED
                    ? 10
                    : 0
                }
                listening
              />
            </Group>
            <Transformer
              ref={transformerRef}
              rotateEnabled={false}
              enabledAnchors={["middle-right"]}
              boundBoxFunc={(oldBox, newBox) =>
                newBox.width < 50 ? oldBox : newBox
              }
              anchorSize={8}
              anchorStroke="blue"
              anchorFill="white"
              anchorCornerRadius={4}
            />
          </Layer>
        </Stage>

        {/* GIF Overlays rendered as HTML elements for native animation */}
        {gifOverlays.map((overlay) => (
          <GifOverlay
            key={overlay.id}
            overlay={overlay}
            containerWidth={videoDimensions.width}
            containerHeight={videoDimensions.height}
            currentTimeMs={currentTimeInMilliseconds}
            onUpdate={onUpdateGif || (() => {})}
          />
        ))}

        {/* Text Overlays */}
        {textOverlays.map((overlay) => (
          <TextOverlayRenderer
            key={overlay.id}
            overlay={overlay}
            containerWidth={videoDimensions.width}
            containerHeight={videoDimensions.height}
            currentTimeMs={currentTimeInMilliseconds}
            onUpdate={onUpdateText || (() => {})}
          />
        ))}
      </Box>
    </Box>
  );
}
